import api from '@/shared/utils/apiConfig';
import type { ApiResponse } from '@/shared/types/api';

export interface LabCatalogActorRef {
  id: string;
  name: string;
}

export interface LabCatalogItem {
  id: string;
  code: string;
  name: string;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  createdBy?: LabCatalogActorRef;
  updatedBy?: LabCatalogActorRef;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateLabCatalogItemPayload {
  name: string;
  sortOrder?: number;
}

export interface UpdateLabCatalogItemPayload {
  name?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface LabCatalogListParams {
  includeInactive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface LabCatalogListResult {
  items: LabCatalogItem[];
  total: number;
}

type CatalogKind = 'work-types' | 'materials' | 'shades';

const buildListParams = (params: LabCatalogListParams = {}) => ({
  includeInactive: params.includeInactive ? 'true' : undefined,
  search: params.search?.trim() || undefined,
  page: params.page,
  limit: params.limit,
});

const catalogApi = (kind: CatalogKind) => {
  const base = `/labo/${kind}`;
  return {
    listResult: async (
      params: LabCatalogListParams = {}
    ): Promise<LabCatalogListResult> => {
      const response = await api.get<ApiResponse<LabCatalogItem[]>>(base, {
        params: buildListParams(params),
      });
      const items = response.data.data ?? [];
      return {
        items,
        total: response.data.total ?? items.length,
      };
    },

    /** Full list for selects / master-data cache (no server pagination). */
    getAll: async (includeInactive = false): Promise<ApiResponse<LabCatalogItem[]>> => {
      const response = await api.get<ApiResponse<LabCatalogItem[]>>(base, {
        params: includeInactive ? { includeInactive: 'true' } : undefined,
      });
      return response.data;
    },

    create: async (
      payload: CreateLabCatalogItemPayload
    ): Promise<ApiResponse<LabCatalogItem>> => {
      const response = await api.post(base, payload);
      return response.data;
    },

    update: async (
      id: string,
      payload: UpdateLabCatalogItemPayload
    ): Promise<ApiResponse<LabCatalogItem>> => {
      const response = await api.put(`${base}/${id}`, payload);
      return response.data;
    },

    deactivate: async (id: string): Promise<ApiResponse<LabCatalogItem>> => {
      const response = await api.delete(`${base}/${id}`);
      return response.data;
    },
  };
};

export const labWorkTypeAPI = catalogApi('work-types');
export const labMaterialAPI = catalogApi('materials');
export const labShadeAPI = catalogApi('shades');
